import { useOrgStore } from '@/stores/org';
import { useListConfigStore } from '@/stores/listConfig';
import { useObjectConfigStore } from '@/stores/objectConfig';
import { useSavedViewsStore } from '@/stores/savedViews';
import { useGridPreferencesStore } from '@/stores/gridPreferences';
import { hasQualifiers, primeQualifiers } from '@/composables/useQualifiers';

/**
 * Ask for everything a list needs as it opens in one request, before it opens.
 *
 * A list opening fired five requests — its filters, its columns, the object's
 * layout, its saved views, the grid preferences — each paying the whole server
 * boot for a few milliseconds of work. A route declares its list in
 * `meta.listBootstrap: { list, object }`; the router calls this as it navigates,
 * while the page's code is still downloading, and hands each store the answer
 * it is about to ask for. The stores keep their own loaders untouched: they find
 * the request already on its way and wait for it.
 *
 * Only what is not already held is asked for, so moving between records of a
 * list asks for nothing. A part the server leaves out, or a failed request,
 * resolves to null and each store falls back to its own endpoint — the worst
 * case is the old behaviour, never a list without its columns.
 *
 * @param {string} org Organization slug from the route.
 * @param {{ list: string, object?: string }} spec
 */
export function primeListBootstrap(org, { list, object = null } = {}) {
    const orgStore = useOrgStore();

    // Only into the organization already set up: a switch resets the stores
    // after this point and would drop what was handed to them.
    if (!org || !list || orgStore.slug !== org) {
        return;
    }

    const listConfig = useListConfigStore();
    const objectConfig = useObjectConfigStore();
    const savedViews = useSavedViewsStore();
    const gridPreferences = useGridPreferencesStore();

    const wanted = {
        qualifiers: !hasQualifiers(list),
        list_config: !listConfig.loaded[list] && !listConfig.loading[list],
        object_config: !!object && !objectConfig.loaded[object] && !objectConfig.loading[object],
        saved_views: !savedViews.views[list] && !savedViews.loading[list],
        grid_preferences: !gridPreferences.loadedAll && !gridPreferences.loadingPromise
    };
    const include = Object.keys(wanted).filter(part => wanted[part]);

    // One part alone is no saving: its own endpoint answers it just as well.
    if (include.length < 2) {
        return;
    }

    const request = axios
        .get(`/api/${org}/list-bootstrap`, { params: { list, object, include: include.join(',') } })
        // An answer for an organization left meanwhile is not handed over.
        .then(({ data }) => (orgStore.slug === org ? data : {}))
        .catch(() => ({}));
    const part = name => request.then(data => data[name] ?? null);

    if (wanted.qualifiers) primeQualifiers(list, part('qualifiers'));
    if (wanted.list_config) listConfig.prime(list, part('list_config'));
    if (wanted.object_config) objectConfig.prime(object, part('object_config'));
    if (wanted.saved_views) savedViews.prime(list, part('saved_views'));
    if (wanted.grid_preferences) gridPreferences.prime(part('grid_preferences'));
}

/**
 * The deepest route record declaring a list, so a record opened inside a list
 * (`/contacts/454`) asks for the list it sits in.
 *
 * @param {import('vue-router').RouteLocationNormalized} to
 * @returns {{ list: string, object?: string }|null}
 */
export function listBootstrapFor(to) {
    const record = [...to.matched].reverse().find(r => r.meta?.listBootstrap);

    return record ? record.meta.listBootstrap : null;
}
